export class GameSimulation {
  constructor(gameInterface, users, width, height) {
    this.gameInterface = gameInterface;
    this.gameTimer = 120000; // 2 minutes in ms
    this.isGameOver = false;
    this.colorChangeInterval = 5000; // 5 seconds in ms
    this.lastColorChange = 0;
    this.colors = ['red', 'blue', 'yellow'];
    
    this.gameState = {
      entities: {},
      colorZones: [],
      screenSize: {
        width,
        height
      },
      worldSize: {
        width: 1200,
        height: 1200
      },
      timeRemaining: this.gameTimer,
      isGameOver: this.isGameOver
    };

    // Create player entities
    Object.keys(users).forEach(userId => {
      this.gameState.entities[userId] = {
        id: userId,
        name: users[userId].name,
        x: 600 + (Math.random() * 200 - 100),
        y: 600 + (Math.random() * 200 - 100),
        dx: 0,
        dy: 0,
        state: 'idle',
        direction: 'right', // Add direction property
        currentColor: 'white',
        nextColor: this.colors[Math.floor(Math.random() * this.colors.length)],
        colorChangeTime: this.colorChangeInterval,
        score: 0,
        speed: 0.15,
        isAlive: true,
        isEliminated: false // Add isEliminated property to track player elimination status
      };
    });

    // Create color zones (fixed positions)
    const zoneCount = 20;
    const colors = ['red', 'blue', 'yellow'];
    
    for (let i = 0; i < zoneCount; i++) {
      // Create zones in a grid-like pattern to ensure good distribution
      const row = Math.floor(i / 5);
      const col = i % 5;
      
      // Add some randomness to the grid positions
      const x = 150 + col * 250 + (Math.random() * 100 - 50);
      const y = 150 + row * 250 + (Math.random() * 100 - 50);
      
      this.gameState.colorZones.push({
        id: `zone-${i}`,
        x,
        y,
        color: colors[Math.floor(Math.random() * colors.length)],
        radius: 50,
        active: true
      });
    }

    this.gameInterface.on('joystick_move', (data) => {
      const entity = this.gameState.entities[data.userId];
      if (entity && entity.isAlive) {
        entity.dx = data.dx;
        entity.dy = data.dy;
        entity.state = (data.dx === 0 && data.dy === 0) ? 'idle' : 'walking';
        if (data.dx !== 0) {
          entity.direction = data.dx > 0 ? 'right' : 'left';
        }
      }
    });
  }

  checkCollision(entity, zone) {
    const distance = Math.sqrt(
      Math.pow(entity.x - zone.x, 2) + 
      Math.pow(entity.y - zone.y, 2)
    );
    return distance < zone.radius + 20; // Player radius is approximately 20
  }

  getResults() {
    const winners = Object.values(this.gameState.entities)
      .filter(e => e.score >= 15)
      .map(p => p.name);
      
    const losers = Object.values(this.gameState.entities)
      .filter(e => e.score < 15 || !e.isAlive)
      .map(p => p.name);
      
    return {
      winners,
      losers
    };
  }

  update(deltaTime) {
    if (this.isGameOver) return;

    // Update timer
    this.gameTimer -= deltaTime;
    this.gameState.timeRemaining = Math.max(0, this.gameTimer);
    
    // Check for game over conditions
    if (this.gameTimer <= 0) {
      this.isGameOver = true;
      this.gameState.isGameOver = true;
      this.gameState.results = this.getResults();
      return;
    }

    // Check if any player has won
    const anyWinner = Object.values(this.gameState.entities).some(entity => entity.score >= 15);
    if (anyWinner) {
      this.isGameOver = true;
      this.gameState.isGameOver = true;
      this.gameState.results = this.getResults();
      return;
    }

    // Update color change timer for all players
    this.lastColorChange += deltaTime;
    if (this.lastColorChange >= this.colorChangeInterval) {
      this.lastColorChange = 0;
      
      // Change colors for all players
      Object.values(this.gameState.entities).forEach(entity => {
        entity.currentColor = entity.nextColor;
        entity.nextColor = this.colors[Math.floor(Math.random() * this.colors.length)];
        entity.colorChangeTime = this.colorChangeInterval;
      });
    } else {
      // Update remaining time until color change
      Object.values(this.gameState.entities).forEach(entity => {
        entity.colorChangeTime = this.colorChangeInterval - this.lastColorChange;
      });
    }

    // Update positions
    Object.values(this.gameState.entities).forEach(entity => {
      if (entity.isAlive) {
        entity.x += entity.dx * deltaTime * entity.speed;
        entity.y += entity.dy * deltaTime * entity.speed;

        // Constrain to world bounds
        entity.x = Math.max(0, Math.min(entity.x, this.gameState.worldSize.width));
        entity.y = Math.max(0, Math.min(entity.y, this.gameState.worldSize.height));
      }
    });

    // Check for collisions with color zones
    Object.values(this.gameState.entities).forEach(entity => {
      if (!entity.isAlive) return;
      
      this.gameState.colorZones.forEach(zone => {
        if (zone.active && this.checkCollision(entity, zone)) {
          if (entity.currentColor === zone.color) {
            // Correct color match
            entity.score += 1;
            
            // Deactivate the zone temporarily and change its color
            zone.active = false;
            setTimeout(() => {
              zone.active = true;
              zone.color = this.colors[Math.floor(Math.random() * this.colors.length)];
            }, 3000);
          } else if (entity.currentColor !== 'white') {
            // Wrong color - player loses
            entity.isAlive = false;
            entity.isEliminated = true; // Mark player as eliminated
            
            // Check if all players are dead
            const allDead = Object.values(this.gameState.entities).every(e => !e.isAlive);
            if (allDead) {
              this.isGameOver = true;
              this.gameState.isGameOver = true;
              this.gameState.results = this.getResults();
            }
          }
        }
      });
    });
  }
}
